/**
 * Подтверждение нового адреса почты по ссылке из письма.
 *
 * Метод публичный: по ссылке нередко переходят из почтового клиента в другом
 * браузере, где сессии нет. Сам токен и есть доказательство.
 */
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useSearchParams } from "react-router-dom";

import { ApiError } from "../api/client";
import { useConfirmEmailChange } from "../api/queries";
import { AuthShell } from "../components/authForm";
import { Spinner } from "../components/ui";
import { useAuthStore } from "../stores/authStore";

type State = "checking" | "done" | "error";

export function ConfirmEmailPage() {
  const { t } = useTranslation();
  const [params] = useSearchParams();
  const token = params.get("token") ?? "";

  const confirm = useConfirmEmailChange();
  const restore = useAuthStore((state) => state.restore);
  const status = useAuthStore((state) => state.status);

  const [state, setState] = useState<State>(token ? "checking" : "error");
  const [message, setMessage] = useState<string | null>(token ? null : t("confirmEmail.noToken"));
  const [email, setEmail] = useState<string>("");

  // Ссылка одноразовая, а в разработке эффекты запускаются дважды: второй
  // запрос получил бы отказ.
  const started = useRef(false);

  useEffect(() => {
    if (!token || started.current) return;
    started.current = true;

    confirm.mutate(token, {
      onSuccess: async (user) => {
        setEmail(user.email ?? "");
        setState("done");
        // Адрес виден в профиле, а признак подтверждения едет в токене.
        if (status === "authenticated") await restore();
      },
      onError: (error) => {
        setState("error");
        setMessage(error instanceof ApiError ? error.message : t("confirmEmail.failed"));
      },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  if (state === "checking") {
    return (
      <AuthShell title={t("confirmEmail.title")} subtitle={t("confirmEmail.checking")}>
        <Spinner />
      </AuthShell>
    );
  }

  if (state === "done") {
    return (
      <AuthShell
        title={t("confirmEmail.doneTitle")}
        subtitle={t("confirmEmail.doneSubtitle", { email })}
        footer={
          <Link to="/profile" className="text-sm text-amber-300 hover:text-amber-200">
            {t("confirmEmail.toProfile")}
          </Link>
        }
      >
        <p className="text-sm text-ink-400">{t("confirmEmail.doneHint")}</p>
      </AuthShell>
    );
  }

  return (
    <AuthShell
      title={t("confirmEmail.errorTitle")}
      subtitle={message ?? t("confirmEmail.failed")}
      footer={
        <Link to="/profile" className="text-sm text-amber-300 hover:text-amber-200">
          {t("confirmEmail.toProfile")}
        </Link>
      }
    >
      <p className="text-sm text-ink-400">{t("confirmEmail.errorHint")}</p>
    </AuthShell>
  );
}
